import type { AllYouCanPlan, BinderType, Store } from '../store/useStore'

/**
 * #2039 伝票の単位 (卓 / バインダー)。定義は services/pay/api/services/binder.py。
 *
 * バインダー形式の店でも、データの上では**バインダー番号が卓番号の枠に入っている**
 * (`tableCount` = 枚数、`tableNumber` = バインダー番号)。会計の経路は卓の店と同じで、
 * 変わるのは画面の表記だけ — 「テーブル 5」ではなく「伝票 5」。
 */

/** バインダーの枚数の上限 (サーバの `binder.MAX_BINDERS` と同じ)。卓は 50。 */
export const MAX_BINDERS = 200
export const MAX_TABLES = 50
export const MAX_BINDER_TYPES = 6
export const MAX_BINDER_LABEL = 20

export const isBinderStore = (store?: Pick<Store, 'billUnit'> | null): boolean =>
  store?.billUnit === 'binder'

/**
 * 卓とバインダーで文言が違うキーの引き方。バインダー形式の店では `<key>Binder` を引く。
 *
 * 文言の中の「テーブル」を画面側で置換しない — 文の形が変わるもの
 * (「テーブルに QR を置く」→「バインダーに QR を貼る」) があり、単語の置き換えでは
 * 不自然な日本語になる。違うものは違うキーとして持つ。
 */
export const unitKey = (store: Pick<Store, 'billUnit'> | null | undefined, key: string): string =>
  isBinderStore(store) ? `${key}Binder` : key

/** その種別を「全員無料」にできるか (= 時間で課金するプランが付いているか) */
export const canBeFree = (
  planId: string | null | undefined, plans: Pick<AllYouCanPlan, 'id' | 'pricing'>[],
): boolean => plans.find((p) => p.id === planId)?.pricing === 'time'

/** 種別の設定の誤り。i18n キーの末尾を返す (`storeSettings.binderTypeError.<key>`)。 */
export function binderTypesProblem(
  types: BinderType[], count: number, plans: Pick<AllYouCanPlan, 'id' | 'pricing'>[],
): string | null {
  const seen: [number, number][] = []
  for (const x of types) {
    if (!x.name.trim()) return 'name'
    if (!(x.start >= 1) || !(x.end >= x.start)) return 'range'
    if (x.end > count) return 'beyondCount'
    if (seen.some(([a, b]) => x.start <= b && a <= x.end)) return 'overlap'
    // 全員無料は時間で課金するプランにだけ効く。定額プランは「料金 × 人数」をそのまま
    // 起こすので、付けられると画面には「無料」と出るのに満額で請求される。
    if (x.allFree && !canBeFree(x.planId, plans)) return 'freeNeedsPlan'
    seen.push([x.start, x.end])
  }
  return null
}
